"use client";

import { cn } from "@newxsystems/shared";
import { Icon } from "@templates/design-system";
import { useId, useState } from "react";
import { SproutRichText } from "../SproutRichText/SproutRichText";
import type { IAccordionListProps } from "./AccordionList.props";

export const AccordionList = ({ items, openBehavior }: IAccordionListProps) => {
  const firstOpen = items.findIndex((item) => item.initiallyOpen);
  const [openItems, setOpenItems] = useState<number[]>(
    openBehavior === "multiple-open"
      ? items.flatMap((item, index) => (item.initiallyOpen ? [index] : []))
      : firstOpen >= 0
        ? [firstOpen]
        : [],
  );
  const baseId = useId();
  const toggle = (index: number) =>
    setOpenItems((current) =>
      current.includes(index)
        ? current.filter((item) => item !== index)
        : openBehavior === "multiple-open"
          ? [...current, index]
          : [index],
    );

  return (
    <div className="sprout-workbook-paper sprout-paper-lift overflow-hidden rounded-[1.5rem_1.5rem_1.5rem_0.35rem] border-2 border-navy-ink">
      {items.map((item, index) => {
        const open = openItems.includes(index);
        const triggerId = `${baseId}-trigger-${index}`;
        const panelId = `${baseId}-panel-${index}`;
        return (
          <div className="border-b-2 border-navy-ink/15 last:border-b-0" key={index}>
            <h3>
              <button
                aria-controls={panelId}
                aria-expanded={open}
                className="flex min-h-20 w-full cursor-pointer items-center justify-between gap-5 px-5 py-5 text-left font-sprout-display text-xl font-bold transition-[background-color,color] hover:bg-sunflower/45 hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-[-3px] focus-visible:outline-ring md:px-7"
                id={triggerId}
                onClick={() => toggle(index)}
                type="button"
              >
                <span>{item.title}</span>
                <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-full border-2 border-navy-ink bg-sunflower">
                  <Icon
                    className={cn("size-4 transition-transform duration-200", open && "rotate-45")}
                    name="plus"
                  />
                </span>
              </button>
            </h3>
            {open ? (
              <div aria-labelledby={triggerId} id={panelId} role="region">
                <SproutRichText
                  className="max-w-[65ch] px-5 pb-7 text-base text-graphite md:px-7 md:text-lg"
                  content={item.content}
                />
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

AccordionList.displayName = "Content::AccordionList";
